import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  CheckCircle2, Inbox, Search, AlertTriangle, CalendarDays,
  Building2, ExternalLink, Briefcase, ArrowRightLeft, XCircle, ChevronDown, ChevronRight,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { Card, Button, Skeleton, LinkFichaPersona } from '../../ui-v2';
import {
  Modal, Badge, EmptyState, Input, Select, ToggleGroup, ToggleItem,
} from '../../ui-v2.5';
import { getMisIntervenciones, reabrirCita } from '../api';
import MarcarCitaModal from './MarcarCitaModal';
import ModalTraspaso from './ModalTraspaso';
import ModalCancelarCita from '../compartidos/ModalCancelarCita';
import {
  agruparPanel, agruparAgenda, tituloDia, filtrarCitas, campanasDe, progresoCita,
} from './panelTecnico';
import { citaYaEmpezo } from '../compartidos/horaMadrid';
import {
  ESTADO_CITA, MODALIDAD, etiquetaDe, esCitaAbierta, esCitaCancelada, textoMotivo,
} from '../compartidos/etiquetasEstado';

/**
 * Panel del técnico · Intervenciones (3/11, B2 y B3). Dos columnas con información DISTINTA:
 *
 *   · Izquierda (~60 %), «Qué tengo que hacer»: «Pasadas sin cerrar» destacadas, «Hoy» por hora y,
 *     debajo, plegadas, «Cerradas» y «Canceladas» (el título es el desplegable). Tarjetas completas
 *     con sus acciones, de ancho acotado (no se estiran con la pantalla).
 *   · Derecha (~40 %), sólo «Mi agenda»: las próximas agrupadas por día («Mañana», «Sábado 7 de
 *     noviembre»…), en filas compactas (hora, persona, campaña, N/M). Al pulsar una fila se
 *     despliegan sus acciones.
 *   · Filtros arriba, a todo el ancho; aplican a todo.
 *   · En móvil se apila: qué hacer (con su historial) y luego la agenda.
 *
 * Días y «hoy» en hora de Madrid (panelTecnico.js / horaMadrid.js), no en UTC.
 */
export default function TabIntervenciones() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marcarTarget, setMarcarTarget] = useState(null);
  const [traspasarTarget, setTraspasarTarget] = useState(null);
  const [reabrirTarget, setReabrirTarget] = useState(null);
  const [cancelarTarget, setCancelarTarget] = useState(null);
  const [reabriendo, setReabriendo] = useState(false);
  const [filtros, setFiltros] = useState({ campana: '', estado: 'todas', persona: '' });

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getMisIntervenciones();
      setData(res.data || []);
    } catch (e) {
      console.error('[TabIntervenciones]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar tus intervenciones.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const campanas = useMemo(() => campanasDe(data), [data]);
  const filtradas = useMemo(() => filtrarCitas(data, filtros), [data, filtros]);
  const grupos = useMemo(() => agruparPanel(filtradas), [filtradas]);
  const agenda = useMemo(() => agruparAgenda(grupos.proximas), [grupos.proximas]);
  const hayFiltros = filtros.campana !== '' || filtros.estado !== 'todas' || filtros.persona.trim() !== '';

  // 3/11 · una realizada vuelve a reservada, con sus subtareas marcadas.
  async function confirmarReabrir() {
    setReabriendo(true);
    try {
      await reabrirCita(reabrirTarget.id);
      toast.success('Cita reabierta: vuelve a reservada.');
      setReabrirTarget(null);
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se ha podido reabrir la cita.');
    } finally {
      setReabriendo(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Cargando tus intervenciones">
        <Skeleton height={40} />
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3 space-y-2"><Skeleton height={96} /><Skeleton height={96} /></div>
          <div className="lg:col-span-2"><Skeleton height={200} /></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm" role="alert">
        {error}
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          icon={Inbox}
          title="No tienes intervenciones asignadas"
          description="Cuando te incluyan como técnico en una campaña, las citas reservadas aparecerán aquí."
        />
      </Card>
    );
  }

  const acciones = (c) => ({
    onMarcar: () => setMarcarTarget(c),
    onTraspasar: () => setTraspasarTarget(c),
    onReabrir: () => setReabrirTarget(c),
    onCancelar: () => setCancelarTarget(c),
  });

  return (
    <div className="space-y-6">
      <Filtros filtros={filtros} setFiltros={setFiltros} campanas={campanas} />

      {filtradas.length === 0 ? (
        <Card className="p-6">
          <EmptyState
            variant="medium"
            title="Ninguna intervención con estos filtros."
            action={<Button type="button" variant="ghost" onClick={() => setFiltros({ campana: '', estado: 'todas', persona: '' })}>Quitar filtros</Button>}
          />
        </Card>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
            {/* Izquierda: qué tengo que hacer */}
            <section className="lg:col-span-3 space-y-5 min-w-0" aria-labelledby="titulo-que-hacer">
              <h2 id="titulo-que-hacer" className="text-base font-semibold text-slate-900">Qué tengo que hacer</h2>

              {grupos.sinCerrar.length > 0 && (
                <Seccion
                  titulo="Pasadas sin cerrar"
                  subtitulo="Ya pasó su hora y siguen abiertas: ciérralas, márcalas como «no asistió» o cancélalas."
                  aviso
                  cuantas={grupos.sinCerrar.length}
                >
                  {grupos.sinCerrar.map((c) => <TarjetaCita key={c.id} cita={c} conFecha {...acciones(c)} />)}
                </Seccion>
              )}

              <Seccion titulo="Hoy" cuantas={grupos.hoy.length}>
                {grupos.hoy.length
                  ? grupos.hoy.map((c) => <TarjetaCita key={c.id} cita={c} {...acciones(c)} />)
                  // Pequeño y pegado al título: sin el padding grande del EmptyState.
                  : <EmptyState variant="minimal" className="px-0 py-1 text-left" title={hayFiltros ? 'Nada para hoy con estos filtros.' : 'Nada para hoy.'} />}
              </Seccion>

              {/* El historial, plegado, en esta misma columna (debajo de «Hoy»). */}
              {grupos.pasadas.length > 0 && (
                <Seccion titulo="Cerradas" cuantas={grupos.pasadas.length} colapsable abiertaAlInicio={filtros.estado === 'cerradas'}>
                  {grupos.pasadas.map((c) => <TarjetaCita key={c.id} cita={c} conFecha {...acciones(c)} />)}
                </Seccion>
              )}
              {grupos.canceladas.length > 0 && (
                <Seccion titulo="Canceladas" cuantas={grupos.canceladas.length} colapsable abiertaAlInicio={filtros.estado === 'canceladas'}>
                  {grupos.canceladas.map((c) => <TarjetaCita key={c.id} cita={c} conFecha {...acciones(c)} />)}
                </Seccion>
              )}
            </section>

            {/* Derecha: mi agenda */}
            <section className="lg:col-span-2 min-w-0" aria-labelledby="titulo-agenda">
              <Card className="p-0 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                  <CalendarDays className="w-4 h-4 text-slate-500" aria-hidden="true" />
                  <h2 id="titulo-agenda" className="text-base font-semibold text-slate-900">Mi agenda</h2>
                  <span className="text-xs text-slate-600">({grupos.proximas.length})</span>
                </div>
                {agenda.length === 0 ? (
                  <div className="p-4">
                    <EmptyState variant="minimal" title={hayFiltros ? 'Sin próximas citas con estos filtros.' : 'Sin próximas citas.'} />
                  </div>
                ) : (
                  agenda.map((dia) => (
                    <div key={dia.fecha}>
                      <h3 className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {dia.titulo} <span className="font-normal normal-case">· {dia.citas.length}</span>
                      </h3>
                      <ul className="divide-y divide-slate-100">
                        {dia.citas.map((c) => <FilaAgenda key={c.id} cita={c} {...acciones(c)} />)}
                      </ul>
                    </div>
                  ))
                )}
              </Card>
            </section>
          </div>
        </>
      )}

      {marcarTarget && (
        <MarcarCitaModal
          cita={marcarTarget}
          onClose={() => setMarcarTarget(null)}
          onOk={async () => {
            setMarcarTarget(null);
            await cargar();
          }}
        />
      )}

      {traspasarTarget && (
        <ModalTraspaso
          cita={traspasarTarget}
          onClose={() => setTraspasarTarget(null)}
          onOk={async () => {
            setTraspasarTarget(null);
            await cargar();
          }}
        />
      )}

      <ModalCancelarCita
        cita={cancelarTarget}
        persona={cancelarTarget?.usuario?.nombre_completo}
        onClose={() => setCancelarTarget(null)}
        onHecho={async () => {
          setCancelarTarget(null);
          toast.success('Cita cancelada. Se ha avisado a la persona para que reserve otra.');
          await cargar();
        }}
      />

      <Modal
        open={!!reabrirTarget}
        onClose={reabriendo ? undefined : () => setReabrirTarget(null)}
        size="sm"
        title="Reabrir la cita"
        subtitle={reabrirTarget?.usuario?.nombre_completo || ''}
        footer={(
          <>
            <Button type="button" variant="ghost" onClick={() => setReabrirTarget(null)} disabled={reabriendo}>Cancelar</Button>
            <Button type="button" onClick={confirmarReabrir} disabled={reabriendo}>{reabriendo ? 'Reabriendo…' : 'Reabrir'}</Button>
          </>
        )}
      >
        <p className="text-sm text-slate-700">
          Vuelve a reservada y la persona, a «cita reservada». Las subtareas marcadas se conservan y
          queda en el historial quién la reabrió. Si la cita aún no ha pasado, se le vuelven a programar
          los recordatorios.
        </p>
      </Modal>
    </div>
  );
}

// --- Subcomponentes ---

function Filtros({ filtros, setFiltros, campanas }) {
  const set = (k) => (v) => setFiltros((f) => ({ ...f, [k]: v }));
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:flex-wrap">
      <Select
        label="Campaña"
        value={filtros.campana}
        onChange={(e) => set('campana')(e.target.value)}
        className="md:min-w-[220px]"
      >
        <option value="">Todas las campañas</option>
        {campanas.map((c) => <option key={c.id} value={String(c.id)}>{c.titulo}</option>)}
      </Select>
      <div>
        <span className="mb-1 block text-xs font-medium text-slate-700">Estado</span>
        <ToggleGroup value={filtros.estado} onChange={set('estado')} className="flex-wrap">
          <ToggleItem value="todas" label="Todas" />
          <ToggleItem value="abiertas" label="Abiertas" />
          <ToggleItem value="cerradas" label="Cerradas" />
          <ToggleItem value="canceladas" label="Canceladas" />
        </ToggleGroup>
      </div>
      <Input
        label="Persona"
        icon={Search}
        placeholder="Buscar por nombre"
        value={filtros.persona}
        onChange={(e) => set('persona')(e.target.value)}
        className="md:min-w-[220px]"
      />
    </div>
  );
}

/**
 * Un bloque con título. Si es `colapsable`, el propio título es el desplegable: un <button> con
 * aria-expanded que ocupa toda la fila, con la flecha delante (▸ cerrado / ▾ abierto). Sin un «Ver»
 * suelto en la otra punta de la pantalla.
 */
function Seccion({ titulo, subtitulo, cuantas, children, aviso, colapsable, abiertaAlInicio = true }) {
  const [abierto, setAbierto] = useState(colapsable ? abiertaAlInicio : true);
  useEffect(() => { if (colapsable) setAbierto(abiertaAlInicio); }, [colapsable, abiertaAlInicio]);
  const idContenido = `seccion-${titulo.replace(/\s+/g, '-').toLowerCase()}`;
  const cabecera = (
    <>
      {aviso && <AlertTriangle className="w-4 h-4" aria-hidden="true" />}
      {colapsable && (abierto
        ? <ChevronDown className="w-4 h-4 text-slate-600" aria-hidden="true" />
        : <ChevronRight className="w-4 h-4 text-slate-600" aria-hidden="true" />)}
      {titulo}
      {cuantas != null && <span className="text-xs font-normal text-slate-600">({cuantas})</span>}
    </>
  );
  const claseTitulo = 'text-sm font-semibold flex items-center gap-1.5 ' + (aviso ? 'text-amber-900' : 'text-slate-800');
  return (
    <section
      className={aviso ? 'rounded-lg border border-amber-300 bg-amber-50 p-3 sm:p-4' : ''}
      aria-label={titulo}
    >
      <div className="mb-2 min-w-0">
        <h3 className={colapsable ? 'm-0' : claseTitulo}>
          {colapsable ? (
            <button
              type="button"
              onClick={() => setAbierto((a) => !a)}
              aria-expanded={abierto}
              aria-controls={idContenido}
              className={claseTitulo + ' w-full rounded px-1 -mx-1 py-1 text-left hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400'}
            >
              {cabecera}
            </button>
          ) : cabecera}
        </h3>
        {subtitulo && <p className={'text-xs mt-0.5 ' + (aviso ? 'text-amber-900' : 'text-slate-600')}>{subtitulo}</p>}
      </div>
      {abierto && <div id={idContenido} className="space-y-2">{children}</div>}
    </section>
  );
}

/** Lo que se puede hacer con una cita: abierta → marcar, cancelar, traspasar; realizada con hora → reabrir. */
function puede(cita) {
  const abierta = esCitaAbierta(cita.estado);
  return {
    abierta,
    // Fase 25 — traspasar solo si: abierta + aún no empezó (hora de Madrid) + campaña con >1 técnico.
    traspasar: abierta && !citaYaEmpezo(cita) && (cita.total_tecnicos || 0) > 1,
    reabrir: !!cita.puede_reabrir,
  };
}

function Acciones({ cita, onMarcar, onTraspasar, onReabrir, onCancelar, className = '' }) {
  const p = puede(cita);
  if (!p.abierta && !p.reabrir) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {p.abierta && (
        <Button type="button" onClick={onMarcar} size="sm">
          <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
          Marcar cita
        </Button>
      )}
      {p.abierta && (
        <Button type="button" onClick={onCancelar} size="sm" variant="outline">
          <XCircle className="w-4 h-4" aria-hidden="true" />
          Cancelar
        </Button>
      )}
      {p.traspasar && (
        <Button type="button" onClick={onTraspasar} size="sm" variant="outline">
          <ArrowRightLeft className="w-4 h-4" aria-hidden="true" />
          Traspasar
        </Button>
      )}
      {p.reabrir && (
        <Button type="button" onClick={onReabrir} size="sm" variant="outline">Reabrir</Button>
      )}
    </div>
  );
}

const horaDe = (c) => (c.hora_inicio
  ? `${c.hora_inicio}–${c.hora_fin || ''}`
  /* Registrada sin cita (24/10): no tiene hora; se dice cómo se hizo. */
  : etiquetaDe(MODALIDAD, c.modalidad).label);

/**
 * Tarjeta de la columna izquierda (y del historial): 2-3 líneas y las acciones al lado, con un
 * ancho máximo para que no se estire con la pantalla. Cancelada: atenuada con fondo y borde
 * apagados, NO con opacity (la insignia perdería contraste).
 */
function TarjetaCita({ cita, conFecha = false, ...acciones }) {
  const meta = etiquetaDe(ESTADO_CITA, cita.estado);
  const cancelada = esCitaCancelada(cita.estado);
  // Las marcas de una cancelada o «no asistió» se conservan, pero no cuentan: sin N/M.
  const progreso = esCitaAbierta(cita.estado) || cita.estado === 'realizada' ? progresoCita(cita) : null;
  const motivo = textoMotivo(cita, progreso);

  return (
    <Card className={'max-w-3xl p-3 sm:p-4 ' + (cancelada ? 'border-slate-200 bg-slate-50' : '')}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 sm:flex-1">
          {/* 1 · hora, persona y estado */}
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className={'text-sm font-semibold tabular-nums ' + (cancelada ? 'text-slate-600' : 'text-slate-900')}>{horaDe(cita)}</span>
            <span className="text-sm">
              <LinkFichaPersona
                personaId={cita.usuario?.id}
                nombre={cita.usuario?.nombre_completo}
                ambito={cita.campana_ambito === 'colaborador' ? 'colaborador' : 'interno'}
              />
            </span>
            <Badge tone={meta.tone}>{meta.label}</Badge>
          </div>
          {/* 2 · día (si no es hoy), campaña, ámbito y progreso */}
          <p className="mt-1 text-xs text-slate-600 flex items-center gap-x-2 gap-y-0.5 flex-wrap">
            {conFecha && <span>{tituloDia(cita.fecha)}</span>}
            <span className="inline-flex items-center gap-1 min-w-0">
              <Briefcase className="w-3 h-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{cita.campana_titulo}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              {cita.campana_ambito === 'colaborador'
                ? <><ExternalLink className="w-3 h-3" aria-hidden="true" /> Colaborador</>
                : <><Building2 className="w-3 h-3" aria-hidden="true" /> Interno</>}
            </span>
            {progreso && (
              <span className={progreso.hechas === progreso.total ? 'text-emerald-700' : ''}>
                Subtareas {progreso.hechas}/{progreso.total}
              </span>
            )}
          </p>
          {/* 3 · el motivo según el estado, o las notas. Texto plano. */}
          {(motivo || (cita.notas && !cancelada)) && (
            <p className="mt-1 text-xs text-slate-700 line-clamp-2">
              {motivo || <span className="italic">“{cita.notas}”</span>}
            </p>
          )}
        </div>
        <Acciones cita={cita} {...acciones} className="sm:flex-col sm:items-stretch sm:shrink-0" />
      </div>
    </Card>
  );
}

/** Fila compacta de «Mi agenda»: hora, persona, campaña y N/M. Al pulsarla, sus acciones. */
function FilaAgenda({ cita, ...acciones }) {
  const [abierta, setAbierta] = useState(false);
  const progreso = progresoCita(cita);
  const meta = etiquetaDe(ESTADO_CITA, cita.estado);
  const idDetalle = `agenda-cita-${cita.id}`;
  return (
    <li>
      <button
        type="button"
        onClick={() => setAbierta((a) => !a)}
        aria-expanded={abierta}
        aria-controls={idDetalle}
        className="w-full grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-center gap-2 px-4 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50"
      >
        <span className="text-sm font-medium tabular-nums text-slate-900">{cita.hora_inicio || '—'}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm text-slate-900">{cita.usuario?.nombre_completo || '—'}</span>
          <span className="block truncate text-xs text-slate-600">{cita.campana_titulo}</span>
        </span>
        <span className="flex items-center gap-2 text-xs text-slate-600 tabular-nums">
          {progreso && <span title="Subtareas hechas">{progreso.hechas}/{progreso.total}</span>}
          {abierta ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
        </span>
      </button>
      {abierta && (
        <div id={idDetalle} className="px-4 pb-3 pl-[4.25rem] space-y-2">
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600">
            <Badge tone={meta.tone}>{meta.label}</Badge>
            <span className="tabular-nums">{cita.hora_inicio}–{cita.hora_fin}</span>
            <LinkFichaPersona
              personaId={cita.usuario?.id}
              nombre="Ver ficha"
              ambito={cita.campana_ambito === 'colaborador' ? 'colaborador' : 'interno'}
            />
          </div>
          <Acciones cita={cita} {...acciones} />
        </div>
      )}
    </li>
  );
}
